import {
  MdArrowBack,
  MdMoreVert,
  MdPhone,
  MdCircle,
} from "react-icons/md";
import getImageUrl from "../../../utils/imageUrl";
import "./ChatHeader.css";

function ChatHeader({
  conversation,
  onBack,
  onCall,
  mode = "provider",
}) {

  // =========================================================
  // NO CONVERSATION
  // =========================================================

  if (!conversation) {
    return null;
  }


  // =========================================================
  // MODE
  //
  // customer:
  // Customer is viewing → show PROVIDER
  //
  // provider:
  // Provider is viewing → show CUSTOMER
  // =========================================================

  const isCustomerMode =
    mode === "customer";


  // =========================================================
  // PROVIDER
  // =========================================================

  const provider =
    conversation.providerId ||
    conversation.provider ||
    null;


  // =========================================================
  // PROVIDER NAME
  // =========================================================

  const providerName =
    provider?.business?.name ||
    provider?.business?.businessName ||
    provider?.businessName ||
    provider?.name ||
    conversation.providerName ||
    "HomeFoods Provider";


  // =========================================================
  // PROVIDER TYPE
  // =========================================================

  const providerType =
    provider?.providerType ||
    provider?.businessType ||
    conversation.providerType ||
    "Food Provider";


  // =========================================================
  // PROVIDER PHONE
  // =========================================================

  const providerPhone =
    provider?.phone ||
    provider?.contact?.phone ||
    provider?.contactNumber ||
    provider?.mobile ||
    provider?.mobileNumber ||
    provider?.userId?.phone ||
    conversation.providerPhone ||
    "";


  // =========================================================
  // PROVIDER IMAGE
  // =========================================================

  const providerImage =
  getImageUrl(
    provider?.photos?.businessLogo ||
    provider?.businessLogo ||
    provider?.photos?.profilePhoto ||
    provider?.profilePhoto ||
    provider?.avatar ||
    conversation.providerImage
  ) || null;


  // =========================================================
  // PROVIDER ONLINE STATUS
  // =========================================================

  const providerOnline =
    provider?.isOnline === true ||
    provider?.online === true ||
    provider?.status === "online";


  // =========================================================
  // CUSTOMER
  // =========================================================

  const customer =
    conversation.customerId ||
    conversation.customer ||
    null;


  // =========================================================
  // CUSTOMER NAME
  // =========================================================

  const customerName =
    customer?.name ||
    customer?.fullName ||
    customer?.ownerName ||
    customer?.username ||
    customer?.user?.name ||
    conversation.customerName ||
    "HomeFoods Customer";


  // =========================================================
  // CUSTOMER PHONE
  // =========================================================

  const customerPhone =
    customer?.phone ||
    customer?.mobile ||
    customer?.mobileNumber ||
    customer?.contactNumber ||
    customer?.userId?.phone ||
    customer?.user?.phone ||
    conversation.customerPhone ||
    "";


  // =========================================================
  // CUSTOMER IMAGE
  //
  // We intentionally return null if there is no image.
  // This allows the initial letter to be displayed.
  // =========================================================

 const customerImage =
  getImageUrl(
    customer?.profilePhoto ||
    customer?.photos?.profilePhoto ||
    customer?.avatar ||
    conversation.customerImage
  ) || null;


  // =========================================================
  // CUSTOMER ONLINE STATUS
  // =========================================================

  const customerOnline =
    customer?.isOnline === true ||
    customer?.online === true ||
    customer?.status === "online";


  // =========================================================
  // FINAL DISPLAY DATA
  //
  // Customer page:
  //    show Provider
  //
  // Provider page:
  //    show Customer
  // =========================================================

  const displayName =
    isCustomerMode
      ? providerName
      : customerName;


  const displayType =
    isCustomerMode
      ? providerType
      : "Customer";


  const displayImage =
    isCustomerMode
      ? providerImage
      : customerImage;


  const phoneNumber =
    isCustomerMode
      ? providerPhone
      : customerPhone;


  const isOnline =
    isCustomerMode
      ? providerOnline
      : customerOnline;


  // =========================================================
  // INITIAL
  //
  // Example:
  // Nithish Nadig → N
  // Chandru Homefoods → C
  // Rithesh Bakery → R
  // =========================================================

  const displayInitial =
    displayName
      ?.trim()
      ?.charAt(0)
      ?.toUpperCase() ||
    "U";


  // =========================================================
  // IMAGE ALT
  // =========================================================

  const imageAlt =
    displayName ||
    "HomeFoods";


  // =========================================================
  // CALL
  // =========================================================

  const handleCall = () => {

    if (!phoneNumber) {
      return;
    }


    if (onCall) {

      onCall(phoneNumber);

      return;

    }


    window.location.href =
      `tel:${phoneNumber}`;

  };


  // =========================================================
  // IMAGE ERROR
  //
  // If image exists in database but URL is broken,
  // hide the image and show the initial.
  // =========================================================

  const handleImageError = (event) => {

    event.currentTarget.style.display =
      "none";


    const fallback =
      event.currentTarget.parentElement
        ?.querySelector(
          ".chat-header-avatar-fallback"
        );


    if (fallback) {

      fallback.style.display =
        "flex";

    }

  };


  // =========================================================
  // JSX
  // =========================================================

  return (

    <header className="chat-header">


      {/* =====================================================
          BACK BUTTON
      ===================================================== */}

      <button
        type="button"
        className="chat-header-back"
        onClick={onBack}
        aria-label="Back to conversations"
      >

        <MdArrowBack />

      </button>


      {/* =====================================================
          PARTICIPANT
      ===================================================== */}

      <div className="chat-header-provider">


        {/* ===================================================
            AVATAR
        =================================================== */}

        <div className="chat-header-avatar">

          {displayImage && (

            <img
              src={displayImage}
              alt={imageAlt}
              onError={handleImageError}
            />

          )}


          {/* =================================================
              INITIAL FALLBACK
          ================================================= */}

          <div
            className="chat-header-avatar-fallback"
            style={{
              display:
                displayImage
                  ? "none"
                  : "flex",
            }}
          >

            {displayInitial}

          </div>


          {/* =================================================
              ONLINE DOT
          ================================================= */}

          {isOnline && (

            <span className="chat-header-online">

              <MdCircle />

            </span>

          )}

        </div>


        {/* ===================================================
            PARTICIPANT INFORMATION
        =================================================== */}

        <div className="chat-header-info">

          <h2>

            {displayName}

          </h2>


          <div className="chat-header-status">

            <span>

              {displayType}

            </span>


            <span className="chat-header-dot">

              •

            </span>


            <span
              className={
                isOnline
                  ? "online-text"
                  : "offline-text"
              }
            >

              {isOnline
                ? "Online"
                : "Offline"}

            </span>

          </div>

        </div>

      </div>


      {/* =====================================================
          ACTIONS
      ===================================================== */}

      <div className="chat-header-actions">


        {/* ===================================================
            CALL
        =================================================== */}

        <button
          type="button"
          className="chat-header-call"
          onClick={handleCall}
          disabled={!phoneNumber}
          aria-label={
            isCustomerMode
              ? "Call provider"
              : "Call customer"
          }
          title={
            phoneNumber
              ? (
                  isCustomerMode
                    ? "Call provider"
                    : "Call customer"
                )
              : (
                  isCustomerMode
                    ? "Provider phone number unavailable"
                    : "Customer phone number unavailable"
                )
          }
        >

          <MdPhone />

        </button>


        {/* ===================================================
            MORE
        =================================================== */}

        <button
          type="button"
          className="chat-header-more"
          aria-label="More options"
          title="More options"
        >

          <MdMoreVert />

        </button>

      </div>

    </header>

  );

}

export default ChatHeader;